import React, { useId } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { RADIUS } from '../../theme/tokens';

/**
 * PillTabs
 * Horizontal pill-style tab selector with a framer-motion sliding active indicator.
 *
 * @param {Array<{ key: string, label: string, icon?: ReactNode }>} tabs
 * @param {string} value - active tab key
 * @param {(key: string) => void} onChange
 * @param {boolean} [fullWidth=false]
 * @param {'filled'|'outline'} [variant='filled']
 */
function PillTabs({ tabs, value, onChange, fullWidth = false, variant = 'filled' }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const layoutId = useId();

  const isOutline = variant === 'outline';

  return (
    <Box
      role="tablist"
      sx={{
        display: 'inline-flex',
        width: fullWidth ? '100%' : 'auto',
        bgcolor: isLight ? 'background.subtle' : 'background.elevated',
        borderRadius: RADIUS.pill,
        p: 0.5,
        gap: 0.25,
        ...(isOutline
          ? { border: `1px solid ${theme.palette.divider}`, bgcolor: 'transparent' }
          : {}),
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === value;
        return (
          <Box
            key={tab.key}
            role="tab"
            aria-selected={isActive}
            aria-current={isActive ? 'page' : undefined}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onChange(tab.key);
            }}
            sx={{
              position: 'relative',
              flex: fullWidth ? 1 : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              px: 2,
              py: 0.75,
              borderRadius: RADIUS.pill,
              cursor: 'pointer',
              userSelect: 'none',
              outline: 'none',
              '&:focus-visible': {
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
              },
              zIndex: 1,
            }}
          >
            {/* Sliding active indicator */}
            {isActive && (
              <motion.div
                layoutId={layoutId}
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: RADIUS.pill,
                  background: isOutline
                    ? theme.palette.primary.soft ?? 'rgba(242,101,34,0.12)'
                    : theme.palette.background.paper,
                  boxShadow: theme.shadowsExt?.sm,
                  zIndex: -1,
                }}
                transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              />
            )}

            {tab.icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isActive ? 'primary.main' : 'text.secondary',
                  '& .MuiSvgIcon-root': { fontSize: 16 },
                }}
              >
                {tab.icon}
              </Box>
            )}
            <Typography
              variant="caption"
              fontWeight={isActive ? 700 : 600}
              color={isActive ? 'primary.main' : 'text.secondary'}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {tab.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default PillTabs;
